import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  fetchMe,
  login as apiLogin,
  register as apiRegister,
  logout as apiLogout,
  googleSession as apiGoogleSession,
  claimPromo,
} from "@/lib/api";

export const PROMO_KEY = "rigcalc_promo";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=anon, obj=auth
  const [showAuth, setShowAuth] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (ref) localStorage.setItem("rigcalc_ref", ref.toUpperCase());
    const promo = params.get("promo");
    if (promo) localStorage.setItem(PROMO_KEY, promo.toUpperCase());
    // Returning from Google sign-in: AuthCallback exchanges the session first.
    if (window.location.hash.includes("session_id=")) return;
    fetchMe()
      .then(setUser)
      .catch((e) => {
        if (e?.response?.status !== 401) console.error("Session check failed", e);
        setUser(false);
      });
  }, []);

  // Attribute the signed-in user to the ad promo code they arrived with.
  const userId = user ? user.id : null;
  useEffect(() => {
    const code = localStorage.getItem(PROMO_KEY);
    if (!userId || !code) return;
    claimPromo(code)
      .catch((e) => console.error("Promo claim failed", e))
      .finally(() => localStorage.removeItem(PROMO_KEY));
  }, [userId]);

  const login = useCallback(async (email, password) => {
    const u = await apiLogin(email, password);
    setUser(u);
    return u;
  }, []);
  const register = useCallback(async (email, password, name, referred_by, source) => {
    const u = await apiRegister(email, password, name, referred_by, source);
    setUser(u);
    return u;
  }, []);
  const googleLogin = useCallback(async (session_id, source, referred_by) => {
    try {
      const u = await apiGoogleSession(session_id, source, referred_by);
      setUser(u);
      return u;
    } catch (e) {
      setUser(false);
      throw e;
    }
  }, []);
  const logout = useCallback(async () => {
    await apiLogout();
    setUser(false);
  }, []);
  const refresh = useCallback(async () => {
    try {
      setUser(await fetchMe());
    } catch (e) {
      console.error("Session refresh failed", e);
      setUser(false);
    }
  }, []);

  const value = useMemo(() => {
    const isAuthed = !!user;
    const now = new Date();
    const hasPro =
      isAuthed &&
      (user.role === "admin" ||
        user.plan === "lifetime" ||
        ((user.plan === "pro" || user.plan === "team") && (!user.plan_expires_at || new Date(user.plan_expires_at) > now)) ||
        (user.plan === "trial" && !!user.trial_ends_at && new Date(user.trial_ends_at) > now));
    const hasGuides =
      hasPro || (isAuthed && user.plan === "guides" && (!user.plan_expires_at || new Date(user.plan_expires_at) > now));
    const isPaid =
      isAuthed && (user.role === "admin" || ["pro", "team", "lifetime"].includes(user.plan));
    return { user, isAuthed, hasPro, hasGuides, isPaid, login, register, googleLogin, logout, refresh, showAuth, setShowAuth };
  }, [user, showAuth, login, register, googleLogin, logout, refresh]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
